import { ApolloQueryResult } from '@apollo/client';
import { useMemo } from 'react';

import { HookResult } from './apolloClient';
import { RGetLanguagesQuery, useGetLanguagesQueryRemote } from '@/store/v2';
import { Language } from '@/types/language';

interface LanguagesResponse extends HookResult {
  languages: readonly Language[];
  refetch: () => Promise<ApolloQueryResult<RGetLanguagesQuery>>;
}

export const useLanguages = (): LanguagesResponse => {
  const { data, error, refetch, loading, networkStatus } = useGetLanguagesQueryRemote();

  /**
   * The schema types every Language field as nullable, so rows missing an id or a name are
   * dropped here instead of widening `Language` for all of its consumers. Memoized because the
   * mapping allocates, and consumers use the result as a hook dependency.
   */
  const languages: Language[] = useMemo(
    () =>
      (data?.getLanguages ?? []).flatMap((language) =>
        language?.id != null && language.name != null ? [{ id: language.id, name: language.name }] : [],
      ),
    [data?.getLanguages],
  );

  return {
    languages,
    error,
    refetch,
    loading,
    networkStatus,
  };
};
